/**
 * VetSupplyBreakdown — V2.K.8
 *
 * Renders a dual-token supply breakdown for VET + VTHO on the
 * VechainMonitor page.
 *
 * Why VeChain deserves a dedicated layout :
 *  - It's the only chain we cover with a true **dual-token** economic
 *    model. Treating it like a single-token breakdown would miss the
 *    most important narrative.
 *  - **VET** is capped at 86.7 B and represents staked value /
 *    governance. Holders mint NFTs via StarGate (min 10 000 VET) to
 *    earn VTHO rewards.
 *  - **VTHO** is the gas token. Post-Hayabusa (April 2025, Renaissance
 *    phase 2), VTHO is generated ONLY from active staking — passive
 *    generation has ended. 100 % of base transaction fees in VTHO
 *    are burned (EIP-1559 style), making VTHO structurally
 *    deflationary as adoption grows.
 *  - **Renaissance roadmap** : 3 phases — Galactica (done), Hayabusa
 *    (live April 2025), Interstellar (next). New Economic Node tiers
 *    Dawn / Lightning / Flash + VeBetterDAO GM NFTs (Venus / Jupiter
 *    / Saturn) layer governance + utility on top.
 *
 * Layout : two side-by-side sub-sections (VET top, VTHO bottom).
 */

import { useTranslation } from 'react-i18next';
import { useVetSupply } from '../../hooks/useVetSupply';

const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'docs' | 'blog' | 'staking' | 'analysis';
  note: string;
}> = [
  {
    label: 'VeChain Dual-Token Economic Model (docs)',
    url: 'https://docs.vechain.org/introduction-to-vechain/dual-token-economic-model',
    category: 'docs',
    note: 'Référence officielle VET + VTHO et mécaniques d\'inter-dépendance.',
  },
  {
    label: 'VeChain Renaissance — Tokenomics Upgrades',
    url: 'https://medium.com/coinmonks/vechain-renaissance-game-changing-tokenomics-upgrades-key-details-da470b740269',
    category: 'blog',
    note: 'Détails Renaissance + 3 phases Galactica / Hayabusa / Interstellar.',
  },
  {
    label: 'StarGate — Staking VeChain (min 10K VET)',
    url: 'https://stargate.vechain.org/',
    category: 'staking',
    note: 'Plateforme staking officielle, mint NFTs comme instruments de délégation.',
  },
  {
    label: 'State of VeChain Q2 2025 — Messari',
    url: 'https://messari.io/report/state-of-vechain-q2-2025',
    category: 'analysis',
    note: 'Rapport indépendant : metrics on-chain, Renaissance progress.',
  },
];

// Sources primaires vérifiées :
//  - github.com/vechain/stargate-contracts (packages/config/mainnet.ts)
//  - github.com/vechain/vebetterdao (monorepo actif, packages/contracts/)
//    Note : l'ancien repo github.com/vechain/vebetterdao-contracts a
//    été archivé en avril 2026. Les adresses correspondent à des
//    proxies (stables après upgrade) et restent valables.
//  - docs.stargate.vechain.org/for-developers/contracts
//
// Audit post-recoupement Claude.ai V2.K.11 → V2.K.12 :
//  - Adresses recollationnées caractère par caractère, conformes
//  - Repo VeBetterDAO archivé → monorepo actif mentionné explicitement
const KNOWN_ADDRESSES: Array<{
  label: string;
  address: string;
  category: 'staking' | 'governance' | 'token' | 'nft';
  note: string;
}> = [
  {
    label: 'StarGate (point d\'entrée)',
    address: '0x03c557be98123fdb6fad325328ac6eb77de7248c',
    category: 'staking',
    note: 'Contrat principal d\'entrée du staking + custody du VET pendant les phases non-déléguées. Source : docs.stargate.vechain.org/for-developers/contracts.',
  },
  {
    label: 'StarGate NFT',
    address: '0x1856c533ac2d94340aaa8544d35a5c1d4a21dee7',
    category: 'nft',
    note: 'Contrat ERC721 représentant les positions de staking. Source : github.com/vechain/stargate-contracts/packages/config/mainnet.ts.',
  },
  {
    label: 'StarGate Delegation',
    address: '0x4cb1c9ef05b529c093371264fab2c93cc6cddb0e',
    category: 'staking',
    note: 'Contrat de délégation qui distribue les rewards VTHO aux holders de NFTs. Source : github.com/vechain/stargate-contracts/packages/config/mainnet.ts.',
  },
  {
    label: 'Legacy Nodes (X / Economic)',
    address: '0xb81E9C5f9644Dec9e5e3Cac86b4461A222072302',
    category: 'nft',
    note: 'Contrat des nœuds économiques pré-Hayabusa (X / Economic Nodes). Source : github.com/vechain/stargate-contracts mainnet.ts.',
  },
  {
    label: 'Node Management',
    address: '0xB0EF9D89C6b49CbA6BBF86Bf2FDf0Eee4968c6AB',
    category: 'staking',
    note: 'Contrat de gestion des nœuds (transitions tiers, upgrades). Source : github.com/vechain/stargate-contracts mainnet.ts.',
  },
  // ─── VeBetterDAO (ecosystem governance + sustainability) ───────────
  {
    label: 'VeBetterDAO — B3TR (token utilité)',
    address: '0x5ef79995FE8a89e0812330E4378eB2660ceDe699',
    category: 'token',
    note: 'Token B3TR du VeBetterDAO (rewards sustainability). Source : monorepo actif github.com/vechain/vebetterdao (proxy address, stable).',
  },
  {
    label: 'VeBetterDAO — VOT3 (token vote)',
    address: '0x76Ca782B59C74d088C7D2Cce2f211BC00836c602',
    category: 'governance',
    note: 'Token de vote VOT3 (gouvernance VeBetterDAO). Source : monorepo actif github.com/vechain/vebetterdao (proxy address, stable).',
  },
  {
    label: 'VeBetterDAO — GalaxyMember (GM NFT)',
    address: '0x93B8cD34A7Fc4f53271b9011161F7A2B5fEA9D1F',
    category: 'nft',
    note: 'ERC721 upgradeable unique pour tous les tiers GM. Venus / Jupiter / Saturn NE SONT PAS des contrats séparés mais des LEVELS dans ce contrat unique — pour le mapping niveau→planète exact, se fier à docs.vebetterdao.org (les billets Medium 2025 utilisent une numérotation différente, source potentielle d\'erreur). Source : monorepo actif github.com/vechain/vebetterdao.',
  },
  {
    label: 'VeBetterDAO — Treasury',
    address: '0xD5903BCc66e439c753e525F8AF2FeC7be2429593',
    category: 'governance',
    note: 'Treasury on-chain VeBetterDAO. Source : monorepo actif github.com/vechain/vebetterdao (proxy address, stable).',
  },
  {
    label: 'VeBetterDAO — Emissions',
    address: '0xDf94739bd169C84fe6478D8420Bb807F1f47b135',
    category: 'governance',
    note: 'Contrat de mint des emissions B3TR. Source : monorepo actif github.com/vechain/vebetterdao (proxy address, stable).',
  },
];

export function VetSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useVetSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtB = (n: number) =>
    `${(n / 1_000_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} Md`;

  return (
    <section
      aria-labelledby="vet-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="vet-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#15BDFF' }}>⬡</span>
          {t('vet.supply.title', 'Supply VeChain — répartition dual-token')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'vet.supply.subtitle',
            'Modèle dual-token : VET (cap 86,7 Md, valeur + staking) génère VTHO (gas, uncapped). Depuis Hayabusa (avril 2025), VTHO est uniquement minté via staking actif, et 100 % des base fees sont burnés.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <VetSection data={data} fmt={fmt} fmtB={fmtB} t={t} />
          <VthoSection data={data} fmt={fmt} fmtB={fmtB} t={t} />
          <RenaissanceNote t={t} />
          <KnownAddresses t={t} />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// VET section (value token, capped)
// ─────────────────────────────────────────────────────────────────────

interface SectionProps {
  data: NonNullable<ReturnType<typeof useVetSupply>['data']>;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function VetSection({ data, fmt, fmtB, t }: SectionProps) {
  const circulatingPct = (data.vetCirculating / data.vetMax) * 100;
  const remainingPct = Math.max(0, 100 - circulatingPct);

  return (
    <div style={{ marginBottom: '16px' }}>
      <SubHeader
        color="#15BDFF"
        title={`VET — ${t('vet.supply.vetSub', 'token de valeur (cap fixe 86,7 Md)')}`}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <StatCard
          label={t('vet.supply.vetCirculating', 'En circulation')}
          value={fmtB(data.vetCirculating)}
          sub={`${fmt(data.vetCirculating)} VET · ${data.vetMintedPct.toFixed(1)} %`}
          color="#15BDFF"
        />
        <StatCard
          label={t('vet.supply.vetRemaining', 'Reste à émettre')}
          value={fmtB(data.vetRemaining)}
          sub={`${(100 - data.vetMintedPct).toFixed(1)} % du cap`}
          color="#f59e0b"
        />
        <StatCard
          label={t('vet.supply.vetMax', 'Cap maximal')}
          value={fmtB(data.vetMax)}
          sub={t('vet.supply.vetMaxNote', 'Fixé par la Foundation')}
          color="#6366f1"
        />
      </div>
      {/* Bar : circulating vs remaining */}
      <div
        aria-label={t(
          'vet.supply.vetBarAlt',
          'Répartition VET sur le cap fixe de 86,7 milliards : circulating vs reste à émettre',
        )}
        role="img"
        style={{
          display: 'flex',
          height: '20px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: `${circulatingPct}%`,
            background: 'linear-gradient(90deg, #0099D6, #15BDFF)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${circulatingPct.toFixed(1)} %`}
        >
          {circulatingPct > 8 ? `${circulatingPct.toFixed(0)} %` : ''}
        </div>
        <div
          style={{
            width: `${remainingPct}%`,
            background:
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.05), rgba(255,255,255,0.05) 6px, rgba(255,255,255,0.10) 6px, rgba(255,255,255,0.10) 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted)',
            fontSize: '10px',
            fontWeight: 600,
            transition: 'width 0.5s ease',
          }}
          title={`${remainingPct.toFixed(1)} %`}
        >
          {remainingPct > 8 ? `${remainingPct.toFixed(1)} %` : ''}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// VTHO section (gas token, uncapped, structurally deflationary)
// ─────────────────────────────────────────────────────────────────────

function VthoSection({ data, fmt, fmtB, t }: SectionProps) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <SubHeader
        color="#FF6F61"
        title={`VTHO — ${t('vet.supply.vthoSub', 'token gas (uncapped, burn 100 % des base fees)')}`}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <StatCard
          label={t('vet.supply.vthoCirculating', 'En circulation')}
          value={fmtB(data.vthoCirculating)}
          sub={`${fmt(data.vthoCirculating)} VTHO`}
          color="#FF6F61"
        />
        <StatCard
          label={t('vet.supply.vthoGenSource', 'Génération post-Hayabusa')}
          value={t('vet.supply.vthoGenStaking', 'Staking only')}
          sub={t('vet.supply.vthoGenNote', 'StarGate — fin de la génération passive')}
          color="#10b981"
        />
        <StatCard
          label={t('vet.supply.vthoBurn', 'Base fees burnés')}
          value="100 %"
          sub={t('vet.supply.vthoBurnNote', 'EIP-1559-style — trajectoire déflationniste')}
          color="#ef4444"
        />
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          padding: '8px 10px',
          background: 'rgba(255, 111, 97, 0.05)',
          border: '1px solid rgba(255, 111, 97, 0.20)',
          borderRadius: '8px',
          lineHeight: 1.5,
        }}
      >
        {t(
          'vet.supply.vthoLegacy',
          'Avant Hayabusa : génération passive de {{rate}} VTHO/jour/VET pour tous les holders. Depuis avril 2025 : uniquement les VET stakés via StarGate (min 10K VET) génèrent du VTHO.',
        ).replace('{{rate}}', String(data.vthoLegacyDailyRate))}
      </div>
    </div>
  );
}

function SubHeader({ color, title }: { color: string; title: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '10px',
        fontSize: '13px',
        fontWeight: 700,
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
      }}
    >
      <span
        aria-hidden
        style={{
          width: '6px',
          height: '20px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {title}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Renaissance callout
// ─────────────────────────────────────────────────────────────────────

function RenaissanceNote({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(21, 189, 255, 0.06)',
        border: '1px dashed rgba(21, 189, 255, 0.35)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#15BDFF' }}>
        {t('vet.supply.renaissanceTitle', 'VeChain Renaissance — 3 phases')}
      </strong>
      <div style={{ marginTop: '4px' }}>
        {t(
          'vet.supply.renaissanceBody',
          'Galactica (livré) → Hayabusa (live avril 2025) → Interstellar (à venir). Hayabusa a introduit StarGate (staking NFTs comme instruments de délégation, min 10K VET, ~5,48 Md VTHO de bonus rewards au launch), de nouveaux tiers de nœuds économiques (Dawn / Lightning / Flash), et la fin de la génération passive de VTHO. VeBetterDAO (sustainability dapps) couvre la gouvernance avec des tiers GM NFT (Venus / Jupiter / Saturn).',
        )}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'vet.supply.renaissanceSource',
          'Sources : docs.vechain.org, news.vechain.org, stargate.vechain.org, Messari State of VeChain Q2 2025.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known on-chain addresses (verified source — github.com/vechain)
// ─────────────────────────────────────────────────────────────────────

function KnownAddresses({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
        marginBottom: '12px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('vet.supply.knownAddressesTitle', 'Adresses on-chain identifiées (mainnet)')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'vet.supply.knownAddressesDisclaimer',
          'Adresses extraites du repo officiel github.com/vechain/stargate-contracts (packages/config/mainnet.ts). Seules les adresses vérifiées en source primaire sont listées.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ADDRESSES.map((entry) => (
          <li
            key={entry.address}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <AddressPill category={entry.category} />
              <strong style={{ color: 'var(--text)' }}>{entry.label}</strong>
              <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
                (VeChainThor mainnet)
              </span>
            </div>
            <code
              style={{
                display: 'block',
                fontSize: '11px',
                color: 'var(--muted)',
                marginTop: '4px',
                wordBreak: 'break-all',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {entry.address}
            </code>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function AddressPill({ category }: { category: 'staking' | 'governance' | 'token' | 'nft' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    staking: { bg: 'rgba(21, 189, 255, 0.18)', color: '#7dd3fc', label: 'Staking' },
    governance: { bg: 'rgba(255, 111, 97, 0.18)', color: '#fb923c', label: 'Gouvernance' },
    token: { bg: 'rgba(16, 185, 129, 0.18)', color: '#34d399', label: 'Token' },
    nft: { bg: 'rgba(167, 139, 250, 0.20)', color: '#c4b5fd', label: 'NFT' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('vet.supply.knownEndpointsTitle', 'Sources officielles & staking')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'vet.supply.knownEndpointsDisclaimer',
          'Le burn 100 % des base fees VTHO est confirmé par la doc officielle (medium.com/@vechainofficial et docs.vechain.org). Le mécanisme exact (déduction protocolaire pure vs transfert vers une adresse nulle) n\'est pas explicitement confirmé en source primaire — à vérifier dans le code consensus VeChainThor si besoin. Les staking NFTs StarGate sont émis par le contrat officiel — surveiller via les rapports Messari pour des chiffres en live.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'docs' | 'blog' | 'staking' | 'analysis' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    docs: { bg: 'rgba(21, 189, 255, 0.18)', color: '#7dd3fc', label: 'Docs' },
    blog: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Blog' },
    staking: { bg: 'rgba(255, 111, 97, 0.18)', color: '#fb923c', label: 'Staking' },
    analysis: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'Analyse' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply VeChain sera affichée dès que l'API répondra.
    </div>
  );
}
